import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';
import { PRODUCT_PLANS_LINKS } from '../../common/constants/stripe_links';
import FormErrorText from '../../common/form-error-text/form-error-text';
import { Helpers } from '../../common/helpers/helpers';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import PricingCard from './PricingCard';
import BillingToggle from './BillingToggle';
import SignOnFeeSection from './SignOnFeeSection';
import EnterpriseSection from './EnterpriseSection';
import FAQSection from './FAQSection';
import { NWClient } from '../../client/NWClient';
import { Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import styles from './scss/pricing_page.module.scss';
import { COUPONS } from '../../common/constants/coupons';
import { useLocation } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;
  const company = useAppSelector(selectAllCompanies)[0];
  const companyLoadingStatus = useAppSelector((state) => state.companies.status);
  const dispatch = useAppDispatch();
  const [termsAndConditionsAgreed, setTermsAndConditionsAgreed] = useState<boolean>(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [pendingStatus, setPendingStatus] = useState(true);
  const [checkingTermsAndConditions, setCheckingTermsAndConditions] = useState(true);
  const [errorTextArray, setErrorTextArray] = useState<any[]>([]);
  const [discountCode, setDiscountCode] = useState<string>('');

  const [isTouched, setIsTouched] = useState(false);

  const isProduction = import.meta.env.VITE_ENV_TYPE === 'PROD';
  const isDev = import.meta.env.VITE_ENV_TYPE === 'DEV';
  const isLocal = import.meta.env.VITE_ENV_TYPE === 'LOCAL';

  const VALID_DISCOUNT_CODES = isProduction
    ? // Prod coupon codes
      COUPONS.PROD_COUPONS
    : isDev
      ? // Dev coupon codes
        COUPONS.DEV_COUPONS
      : isLocal
        ? // Coupon codes for local testing, Devs insert their own coupon codes here
          COUPONS.LOCAL_COUPONS
        : // UAT coupon codes
          COUPONS.UAT_COUPONS;

  const [isApaDiscounted, setIsApaDiscounted] = useState(false);
  const [isEssentialsCouponApplied, setIsEssentialsCouponApplied] = useState(false);
  const [isGrowthCouponApplied, setIsGrowthCouponApplied] = useState(false);

  const [isSignOnVisible, setIsSignOnVisible] = useState(true);
  const [isGrowthVisible, setIsGrowthVisible] = useState(true);

  const [isOpen, setIsOpen] = useState(false);

  const [showBillingToggle, setShowBillingToggle] = useState(true);

  // Payment links for pricing tiers
  const paymentLinks = useMemo(
    () => ({
      free: 'https://businesskitz.com/signup',
      esign_only: isProduction
        ? PRODUCT_PLANS_LINKS.PROD[
            billingPeriod === 'annual'
              ? 'ESIGN_ONLY_ANNUAL'
              : discountCode === 'ESSA35'
                ? 'ESIGN_ONLY_ESSA'
                : 'ESIGN_ONLY'
          ]
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV[
              billingPeriod === 'annual'
                ? 'ESIGN_ONLY_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'ESIGN_ONLY_ESSA'
                  : 'ESIGN_ONLY'
            ]
          : PRODUCT_PLANS_LINKS.TEST[
              billingPeriod === 'annual'
                ? 'ESIGN_ONLY_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'ESIGN_ONLY_ESSA'
                  : 'ESIGN_ONLY'
            ],
      essentials: isProduction
        ? PRODUCT_PLANS_LINKS.PROD[
            billingPeriod === 'annual'
              ? 'BUSINESS_ESSENTIALS_ANNUAL'
              : discountCode === 'ESSA35'
                ? 'BUSINESS_ESSENTIALS_ESSA'
                : 'BUSINESS_ESSENTIALS'
          ]
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV[
              billingPeriod === 'annual'
                ? 'BUSINESS_ESSENTIALS_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'BUSINESS_ESSENTIALS_ESSA'
                  : 'BUSINESS_ESSENTIALS'
            ]
          : PRODUCT_PLANS_LINKS.TEST[
              billingPeriod === 'annual'
                ? 'BUSINESS_ESSENTIALS_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'BUSINESS_ESSENTIALS_ESSA'
                  : 'BUSINESS_ESSENTIALS'
            ],
      growth: isProduction
        ? PRODUCT_PLANS_LINKS.PROD[
            billingPeriod === 'annual'
              ? 'BUSINESS_GROWTH_ANNUAL'
              : discountCode === 'ESSA35'
                ? 'BUSINESS_GROWTH_ESSA'
                : 'BUSINESS_GROWTH'
          ]
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV[
              billingPeriod === 'annual'
                ? 'BUSINESS_GROWTH_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'BUSINESS_GROWTH_ESSA'
                  : 'BUSINESS_GROWTH'
            ]
          : PRODUCT_PLANS_LINKS.TEST[
              billingPeriod === 'annual'
                ? 'BUSINESS_GROWTH_ANNUAL'
                : discountCode === 'ESSA35'
                  ? 'BUSINESS_GROWTH_ESSA'
                  : 'BUSINESS_GROWTH'
            ],
      apa_discounted_essentials: isProduction
        ? PRODUCT_PLANS_LINKS.PROD['APA_DISCOUNTED_ESSENTIALS']
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV['APA_DISCOUNTED_ESSENTIALS']
          : PRODUCT_PLANS_LINKS.TEST['APA_DISCOUNTED_ESSENTIALS'],
      free_essentials: isProduction
        ? PRODUCT_PLANS_LINKS.PROD['APA_FREE_ESSENTIALS']
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV['APA_FREE_ESSENTIALS']
          : PRODUCT_PLANS_LINKS.TEST['APA_FREE_ESSENTIALS'],
      free_growth: isProduction
        ? PRODUCT_PLANS_LINKS.PROD['APA_FREE_GROWTH']
        : isDev
          ? PRODUCT_PLANS_LINKS.DEV['APA_FREE_GROWTH']
          : PRODUCT_PLANS_LINKS.TEST['APA_FREE_GROWTH'],
    }),
    [isProduction, billingPeriod, discountCode, isDev]
  );

  const handleDiscountCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(event.target.value);
  };

  const handleOnDemandRendering = () => {
    if (
      COUPONS.SIGNON_FEE_COUPONS.includes(discountCode) ||
      COUPONS.SIGNON_FEE_COUPONS_OFF_50_WITH_TAX.includes(discountCode) ||
      discountCode === ''
    ) {
      setIsSignOnVisible(true);
    } else {
      setIsSignOnVisible(false);
    }

    if (COUPONS.GROWTH_COUPONS.includes(discountCode) || discountCode === '') {
      setIsGrowthVisible(true);
    } else {
      setIsGrowthVisible(false);
    }
  };

  const handleConfirmClick = () => {
    // To be refactored
    // Initially, The plan was to add APA coupons only, hence the apa_free & apa_discounted
    // apa_free is now including all non 4$ coupons

    // Coupons that set the plan to 4$

    if (COUPONS.FOUR_DOLLAR_COUPONS.includes(discountCode)) {
      setIsApaDiscounted(true);
      setIsEssentialsCouponApplied(false);
      setIsSignOnVisible(false);
      setIsGrowthVisible(false);
      setBillingPeriod('monthly');
      setIsOpen(false);
      setShowBillingToggle(false);
    }

    // 100 OFF, 50 OFF, 25 OFF COUPONS

    if (
      VALID_DISCOUNT_CODES.includes(discountCode) &&
      !COUPONS.FOUR_DOLLAR_COUPONS.includes(discountCode)
    ) {
      if (COUPONS.ESSENTIALS_COUPONS.includes(discountCode)) {
        setIsEssentialsCouponApplied(true);
      }
      if (COUPONS.GROWTH_COUPONS.includes(discountCode)) {
        setIsGrowthCouponApplied(true);
      }
      handleOnDemandRendering();
      setIsApaDiscounted(false);
      setBillingPeriod('monthly');
      setIsOpen(false);
      setShowBillingToggle(false);
    }

    // Add more conditions for other discount codes if needed
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  useEffect(() => {
    if (!company) {
      dispatch(fetchCompanies())
        .unwrap()
        .then(() => {
          setPendingStatus(false);
        })
        .catch((error) => {
          Helpers.handleRequestError(error?.response, setErrorTextArray, setPendingStatus);
        });
    } else {
      startTransition(() => setPendingStatus(false));
    }
  }, [company, dispatch]);

  useEffect(() => {
    NWClient.subscriptionTermsCheck()
      .then((res: any) => {
        setTermsAndConditionsAgreed(res.is_accepted === true);
      })
      .catch(() => setTermsAndConditionsAgreed(false))
      .finally(() => setCheckingTermsAndConditions(false));
  }, [path]);

  return (
    <React.Fragment>
      {isOpen && (
        <Grid component='div' container className={styles.modal_container}>
          <Grid component='div' size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
            <Card>
              <CardContent className='space-y-1'>
                <Typography variant='h6'>Use a coupon</Typography>
                <TextField
                  sx={{
                    marginTop: '0.5rem',
                    '& .MuiInputBase-input::placeholder': {
                      color: isTouched && !discountCode ? 'red' : 'inherit',
                      opacity: 0.6,
                    },
                  }}
                  value={discountCode}
                  error={
                    (isTouched && discountCode === '') ||
                    (isTouched && !VALID_DISCOUNT_CODES.includes(discountCode))
                  }
                  onChange={handleDiscountCodeChange}
                  placeholder='Enter discount code'
                  variant='outlined'
                  fullWidth
                  onBlur={handleBlur}
                />
                <div className={styles.button_container}>
                  <Button
                    variant='contained'
                    className='btn-default btn-action-basic btn-cancel'
                    onClick={() => setIsOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    variant='contained'
                    className='btn-default btn-action-basic'
                    onClick={handleConfirmClick}
                  >
                    Confirm
                  </Button>
                </div>
                {/* <SocialMediaForm onClose={onClose} onConfirm={onConfirm} /> */}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {companyLoadingStatus === 'succeeded' && (
        <div className='pricingContainer'>
          {/* Header Section */}
          <header className='headerSection'>
            <h1 className='title'>{t('pricing.header')}</h1>
            <p className='subtitle'>{t('pricing.subheader')}</p>
            <p className='description'>{t('pricing.description')}</p>
          </header>
          <div className={styles.coupon_button}>
            <Button
              variant='contained'
              className='btn-default btn-action-basic btn-primary-action'
              onClick={() => setIsOpen(true)}
            >
              {t('pricing.couponButton')}
            </Button>
          </div>
          {/* Billing Toggle */}
          {showBillingToggle && (
            <BillingToggle billingPeriod={billingPeriod} setBillingPeriod={setBillingPeriod} />
          )}

          <div className='pricing_grid_wrapper'>
            <div className='pricingGrid'>
              <PricingCard
                plan='free'
                billingPeriod={billingPeriod}
                paymentLinks={paymentLinks}
                termsAndConditionsAgreed={termsAndConditionsAgreed}
              />
              <PricingCard
                plan='esign_only'
                discount_code={discountCode}
                billingPeriod={billingPeriod}
                paymentLinks={paymentLinks}
                termsAndConditionsAgreed={termsAndConditionsAgreed}
              />
              <PricingCard
                plan={
                  isApaDiscounted
                    ? 'apa_discounted_essentials'
                    : isEssentialsCouponApplied &&
                        !COUPONS.SIGNON_FEE_COUPONS.includes(discountCode) &&
                        discountCode !== 'ESSA35'
                      ? 'free_essentials'
                      : 'essentials'
                }
                discount_code={discountCode}
                billingPeriod={billingPeriod}
                paymentLinks={paymentLinks}
                termsAndConditionsAgreed={termsAndConditionsAgreed}
              />
              {isGrowthVisible && (
                <PricingCard
                  plan={
                    isGrowthCouponApplied &&
                    !COUPONS.SIGNON_FEE_COUPONS.includes(discountCode) &&
                    discountCode !== 'ESSA35'
                      ? 'free_growth'
                      : 'growth'
                  }
                  discount_code={discountCode}
                  billingPeriod={billingPeriod}
                  paymentLinks={paymentLinks}
                  termsAndConditionsAgreed={termsAndConditionsAgreed}
                />
              )}
            </div>
          </div>

          {/* Sign On Fee Section */}
          {isSignOnVisible && <SignOnFeeSection discount_code={discountCode} />}

          {/* Enterprise Section */}
          {discountCode === '' && <EnterpriseSection />}

          {/* FAQ Section */}
          <FAQSection />
        </div>
      )}

      {/* Error Messages */}
      <FormErrorText textCentered={true} errorTextArray={errorTextArray} />

      {/* Loading Overlay */}
      {(pendingStatus || checkingTermsAndConditions) && <ProgressOverlay />}
    </React.Fragment>
  );
};

export default PricingPage;
